import React, { useEffect, useRef, useState } from "react";
import { FiX, FiSend, FiCheck, FiAlertTriangle } from "react-icons/fi";

import styles from "./ToolFeedback.module.css";
import { clean, classifyContact, submitLead } from "../../lib/leads";

/* Feedback from inside a free tool.
 *
 * A modal rather than a link out: somebody who just hit a split this tool
 * cannot write is standing at a POS, and sending them to a contact page loses
 * both the thought and the tester. It posts to the same lead endpoint as the
 * homepage form — one payload shape, one inbox — with the tool's name on the
 * front of the message so a note arrives saying what it is about.
 *
 * Copy comes from the calling tool's strings bundle, so this stays reusable
 * and the tool keeps its own voice (and, later, its own Spanish).
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   toolName: string,
 *   labels: object,
 *   returnFocusTo?: React.RefObject<HTMLElement>,
 * }} props
 */
const ToolFeedback = ({ open, onClose, toolName, labels, returnFocusTo }) => {
  const [form, setForm] = useState({ name: "", contact: "", message: "", company: "" });
  const [status, setStatus] = useState("idle"); // idle | invalid | sending | sent | error
  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);

  const updateField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // Opening puts the caret in the first box and closing hands focus back to
  // the button that opened it, so a keyboard never gets dropped on the page
  // behind the dialog.
  useEffect(() => {
    if (!open) return undefined;
    // Read now, not in the cleanup: by then the dialog is unmounting and the
    // trigger is what we still want to hand the thumb back to.
    const trigger = (returnFocusTo && returnFocusTo.current) || document.activeElement;
    if (firstFieldRef.current) firstFieldRef.current.focus();
    // The page behind must not scroll under the dialog on a phone.
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = priorOverflow;
      if (trigger && trigger.focus) trigger.focus();
    };
  }, [open, returnFocusTo]);

  // Escape closes. Tab cycles inside the dialog rather than wandering off into
  // the calculator underneath it.
  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab" || !dialogRef.current) return;
    const focusable = Array.from(
      dialogRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), a[href]'
      )
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (form.company) return; // bot filled the honeypot

    const name = clean(form.name);
    const message = clean(form.message);
    const contact = classifyContact(form.contact);
    if (!name || !message || !contact.ok) {
      setStatus("invalid");
      return;
    }

    setStatus("sending");
    try {
      await submitLead({
        name,
        contact,
        // The tool's name rides on the message so the note is readable on its
        // own, wherever it is read, without the reader cross-checking a page
        // path against a list of tools.
        message: `[${toolName}] ${message}`,
      });
      setStatus("sent");
      setForm({ name: "", contact: "", message: "", company: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  if (!open) return null;

  return (
    <div
      className={styles.backdrop}
      // A click that starts inside the dialog and ends on the backdrop is a
      // drag, not a dismissal, so only a click landing on the backdrop itself
      // closes it. Losing a typed paragraph to a stray drag is unforgivable.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={onKeyDown}
    >
      <div
        className={styles.dialog}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tool-feedback-title"
      >
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={labels.close}
        >
          <FiX aria-hidden="true" />
        </button>

        <p className={styles.eyebrow}>{labels.eyebrow}</p>
        <h2 className={styles.title} id="tool-feedback-title">
          {labels.title}
        </h2>

        {status === "sent" ? (
          <div className={styles.done} role="status">
            <FiCheck className={styles.doneIcon} aria-hidden="true" />
            <p className={styles.doneText}>{labels.sent}</p>
            <button type="button" className={styles.secondary} onClick={onClose}>
              {labels.backToTool}
            </button>
          </div>
        ) : (
          <>
            <p className={styles.sub}>{labels.sub}</p>
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{labels.name}</span>
                <input
                  className={styles.input}
                  type="text"
                  name="name"
                  ref={firstFieldRef}
                  value={form.name}
                  onChange={updateField}
                  autoComplete="name"
                  required
                />
              </label>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>{labels.contact}</span>
                <input
                  className={styles.input}
                  type="text"
                  name="contact"
                  value={form.contact}
                  onChange={updateField}
                  autoComplete="on"
                  placeholder={labels.contactPlaceholder}
                  required
                />
              </label>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>{labels.message}</span>
                <textarea
                  className={styles.textarea}
                  name="message"
                  rows="5"
                  value={form.message}
                  onChange={updateField}
                  placeholder={labels.messagePlaceholder}
                  required
                />
              </label>

              {/* honeypot: hidden from people, tempting to bots */}
              <input
                className={styles.hp}
                type="text"
                name="company"
                tabIndex="-1"
                autoComplete="off"
                value={form.company}
                onChange={updateField}
                aria-hidden="true"
              />

              {status === "invalid" && (
                <p className={styles.note}>
                  <FiAlertTriangle aria-hidden="true" /> {labels.invalid}
                </p>
              )}
              {status === "error" && (
                <p className={styles.note}>
                  <FiAlertTriangle aria-hidden="true" /> {labels.error}
                </p>
              )}

              <div className={styles.actions}>
                <button
                  type="submit"
                  className={styles.primary}
                  disabled={status === "sending"}
                >
                  <FiSend aria-hidden="true" />{" "}
                  {status === "sending" ? labels.sending : labels.send}
                </button>
                <button type="button" className={styles.ghost} onClick={onClose}>
                  {labels.cancel}
                </button>
              </div>

              <p className={styles.privacy}>{labels.privacy}</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ToolFeedback;
